import { useState } from "react";
import styles from "./GeneralSettings.module.scss";
import ChevronDownIcon from "@/components/SVGComponents/ChevronDownIcon";
import ToggleSwitch from "@/components/ToggleSwitch/ToggleSwitch";

interface ToggleItem {
  id: string;
  title: string;
  description?: string;
  enabled: boolean;
}

const GeneralSettings = () => {
  // Form fields state
  const [formData, setFormData] = useState({
    clubName: "The Chartered",
    legalEntity: "Empirilex Private Limited",
    supportEmail: "concierge@thecharter.in",
    timeZone: "Asia/Kolkata (GMT+5:30)",
    currency: "Indian Rupee (₹)",
    financialYear: "April – March",
  });

  // Toggles state for Sign-in & Applications
  const [signInToggles, setSignInToggles] = useState<ToggleItem[]>([
    {
      id: "linkedin",
      title: "Continue with LinkedIn",
      description: "Shown first on the sign-in screen",
      enabled: true,
    },
    {
      id: "google",
      title: "Continue with Google",
      description: "Shown second",
      enabled: true,
    },
    {
      id: "otp",
      title: "Email or phone with OTP",
      description: "Always available, cannot be turned off",
      enabled: true,
    },
  ]);

  // Toggles state for Member Directory
  const [directoryToggles, setDirectoryToggles] = useState<ToggleItem[]>([
    {
      id: "name_photo",
      title: "Name and photo",
      description: "Always shown, cannot be turned off",
      enabled: true,
    },
    {
      id: "company_role",
      title: "Company and role",
      enabled: true,
    },
    {
      id: "city_chapter",
      title: "City and chapter",
      enabled: true,
    },
    {
      id: "email_address",
      title: "Email address",
      description: "Members can request an introduction instead",
      enabled: false,
    },
    {
      id: "phone_number",
      title: "Phone number",
      enabled: false,
    },
  ]);

  // Toggles state for Applicants awaiting decision
  const [applicantsToggles, setApplicantsToggles] = useState<ToggleItem[]>([
    {
      id: "past_events_preview",
      title: "Can see the past events preview",
      description: "Limited feed, marked as a preview",
      enabled: true,
    },
    {
      id: "gathering_tickets",
      title: "Can buy gathering tickets",
      description: "Off means tickets require an accepted membership",
      enabled: false,
    },
    {
      id: "member_directory",
      title: "Can see the member directory",
      enabled: false,
    },
  ]);

  const handleFieldChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleToggleSignIn = (id: string) => {
    setSignInToggles((prev) =>
      prev.map((item) => (item.id === id ? { ...item, enabled: !item.enabled } : item)),
    );
  };

  const handleToggleDirectory = (id: string) => {
    setDirectoryToggles((prev) =>
      prev.map((item) => (item.id === id ? { ...item, enabled: !item.enabled } : item)),
    );
  };

  const handleToggleApplicants = (id: string) => {
    setApplicantsToggles((prev) =>
      prev.map((item) => (item.id === id ? { ...item, enabled: !item.enabled } : item)),
    );
  };

  return (
    <div className={styles.generalContainer}>
      {/* 1. GENERAL CARD */}
      <section className={styles.surfaceCard}>
        <div className={styles.cardHeader}>
          <h2 className={styles.cardTitle}>General</h2>
          <span className={styles.savedBadge}>
            <span className={styles.savedDot} />
            SAVED
          </span>
        </div>

        <div className={styles.fieldsGrid}>
          <div className={styles.formGroup}>
            <label className={styles.fieldLabel} htmlFor="clubName">
              CLUB NAME
            </label>
            <input
              id="clubName"
              type="text"
              className={styles.textInput}
              value={formData.clubName}
              onChange={(e) => handleFieldChange("clubName", e.target.value)}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.fieldLabel} htmlFor="legalEntity">
              LEGAL ENTITY
            </label>
            <input
              id="legalEntity"
              type="text"
              className={styles.textInput}
              value={formData.legalEntity}
              onChange={(e) => handleFieldChange("legalEntity", e.target.value)}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.fieldLabel} htmlFor="supportEmail">
              SUPPORT EMAIL
            </label>
            <input
              id="supportEmail"
              type="email"
              className={styles.textInput}
              value={formData.supportEmail}
              onChange={(e) => handleFieldChange("supportEmail", e.target.value)}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.fieldLabel} htmlFor="timeZone">
              TIME ZONE
            </label>
            <div className={styles.selectWrapper}>
              <select
                id="timeZone"
                className={styles.selectInput}
                value={formData.timeZone}
                onChange={(e) => handleFieldChange("timeZone", e.target.value)}
              >
                <option value="Asia/Kolkata (GMT+5:30)">Asia/Kolkata (GMT+5:30)</option>
                <option value="UTC (GMT+0:00)">UTC (GMT+0:00)</option>
                <option value="America/New_York (EST)">America/New_York (EST)</option>
                <option value="Europe/London (GMT)">Europe/London (GMT)</option>
              </select>
              <ChevronDownIcon className={styles.selectChevron} />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.fieldLabel} htmlFor="currency">
              CURRENCY
            </label>
            <div className={styles.selectWrapper}>
              <select
                id="currency"
                className={styles.selectInput}
                value={formData.currency}
                onChange={(e) => handleFieldChange("currency", e.target.value)}
              >
                <option value="Indian Rupee (₹)">Indian Rupee (₹)</option>
                <option value="US Dollar ($)">US Dollar ($)</option>
                <option value="Euro (€)">Euro (€)</option>
                <option value="British Pound (£)">British Pound (£)</option>
              </select>
              <ChevronDownIcon className={styles.selectChevron} />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.fieldLabel} htmlFor="financialYear">
              FINANCIAL YEAR
            </label>
            <div className={styles.selectWrapper}>
              <select
                id="financialYear"
                className={styles.selectInput}
                value={formData.financialYear}
                onChange={(e) => handleFieldChange("financialYear", e.target.value)}
              >
                <option value="April – March">April – March</option>
                <option value="January – December">January – December</option>
                <option value="July – June">July – June</option>
              </select>
              <ChevronDownIcon className={styles.selectChevron} />
            </div>
          </div>
        </div>
      </section>

      {/* 2. SIGN-IN & APPLICATIONS CARD */}
      <section className={styles.surfaceCard}>
        <div className={styles.cardTitleBlock}>
          <h2 className={styles.cardTitle}>Sign-in & Applications</h2>
          <p className={styles.cardSubtitle}>
            How members get in, and what the app promises applicants.
          </p>
        </div>

        <div className={styles.toggleList}>
          {signInToggles.map((item) => (
            <label key={item.id} className={styles.toggleRow}>
              <div className={styles.rowInfo}>
                <span className={styles.rowTitle}>{item.title}</span>
                {item.description && (
                  <span className={styles.rowDescription}>{item.description}</span>
                )}
              </div>
              <ToggleSwitch
                checked={item.enabled}
                onChange={() => handleToggleSignIn(item.id)}
                aria-label={item.title}
              />
            </label>
          ))}
        </div>

        <div className={styles.slaSection}>
          <div className={styles.slaColumn}>
            <span className={styles.slaLabel}>APPLICATION REVIEW SLA</span>
            <span className={styles.slaValue}>48 hours</span>
            <span className={styles.slaCaption}>
              Drives the past-SLA count on the Dashboard
            </span>
          </div>

          <div className={styles.slaColumn}>
            <span className={styles.slaLabel}>SHOWN TO APPLICANTS AS</span>
            <span className={styles.slaQuote}>"Membership review takes 24 to 48 hours"</span>
            <span className={styles.slaCaption}>Edit here, it appears in the app</span>
          </div>
        </div>

        <div className={styles.calloutBanner}>
          19 applications are currently past this SLA, the oldest by 11 days. Raising the
          SLA changes what applicants are promised.
        </div>
      </section>

      {/* 3. DIRECTORY & APPLICANTS CARD */}
      <section className={styles.surfaceCard}>
        <div className={styles.cardTitleBlock}>
          <h2 className={styles.cardTitle}>Directory & Applicants</h2>
          <p className={styles.cardSubtitle}>
            What members can see about each other, and what applicants can do before
            acceptance.
          </p>
        </div>

        <div>
          <span className={styles.subSectionHeader}>SHOWN IN THE MEMBER DIRECTORY</span>
          <div className={styles.toggleList}>
            {directoryToggles.map((item) => (
              <label key={item.id} className={styles.toggleRow}>
                <div className={styles.rowInfo}>
                  <span className={styles.rowTitle}>{item.title}</span>
                  {item.description && (
                    <span className={styles.rowDescription}>{item.description}</span>
                  )}
                </div>
                <ToggleSwitch
                  checked={item.enabled}
                  onChange={() => handleToggleDirectory(item.id)}
                  aria-label={item.title}
                />
              </label>
            ))}
          </div>
        </div>

        <div>
          <span className={styles.subSectionHeader}>APPLICANTS AWAITING A DECISION</span>
          <div className={styles.toggleList}>
            {applicantsToggles.map((item) => (
              <label key={item.id} className={styles.toggleRow}>
                <div className={styles.rowInfo}>
                  <span className={styles.rowTitle}>{item.title}</span>
                  {item.description && (
                    <span className={styles.rowDescription}>{item.description}</span>
                  )}
                </div>
                <ToggleSwitch
                  checked={item.enabled}
                  onChange={() => handleToggleApplicants(item.id)}
                  aria-label={item.title}
                />
              </label>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default GeneralSettings;
